import { useState } from 'react';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { PhotoUpload } from '@/components/shared/PhotoUpload';
import { Avatar } from '@/components/shared/Avatar';
import { CropPhotoEditor } from '@/components/shared/CropPhotoEditor';
import type { MeProfile } from '@/types/profile.types';
import { InfoRow, ManagedByAdminNote, ProfileSectionCard } from './ProfileSectionCard';

export interface IdentitySectionProps {
  me: MeProfile;
  uploading: boolean;
  /** Receives the CROPPED file; resolves true when the upload succeeded (closes the editor). */
  onPhotoUpload: (file: File) => Promise<boolean>;
  onPhotoReject: (message: string) => void;
  onPhotoRemove: () => void;
}

/**
 * Identity: photo, names, role chips. Names are read-only in P1 (OD-1 default,
 * `capabilities.canEditNames` is false); the photo is self-service when
 * `capabilities.canUploadPhoto` allows it.
 *
 * PP-1: choosing a file no longer uploads it. The file opens the crop editor;
 * only "Save photo" hands the exported square to `onPhotoUpload`, and Cancel
 * discards without a request.
 */
export function IdentitySection({
  me,
  uploading,
  onPhotoUpload,
  onPhotoReject,
  onPhotoRemove,
}: IdentitySectionProps) {
  const displayName = `${me.firstName} ${me.lastName}`.trim() || me.username || 'User';
  const [pending, setPending] = useState<File | null>(null);
  return (
    <ProfileSectionCard title="Identity" icon={faUser} testId="profile-identity">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        {me.capabilities.canUploadPhoto ? (
          <>
            <PhotoUpload
              firstName={me.firstName}
              lastName={me.lastName}
              src={me.profilePhotoUrl}
              uploading={uploading}
              onSelect={setPending}
              onReject={onPhotoReject}
              onRemove={onPhotoRemove}
            />
            <CropPhotoEditor
              file={pending}
              open={pending !== null}
              saving={uploading}
              firstName={me.firstName}
              lastName={me.lastName}
              onCancel={() => setPending(null)}
              onConfirm={async (cropped) => {
                const ok = await onPhotoUpload(cropped);
                if (ok) setPending(null);
                return ok;
              }}
            />
          </>
        ) : (
          <Avatar
            firstName={me.firstName}
            lastName={me.lastName}
            src={me.profilePhotoUrl}
            size="xl"
          />
        )}
        <div className="flex-1 space-y-3">
          <div>
            <p className="text-lg font-semibold text-foreground">{displayName}</p>
            {me.roles.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {me.roles.map((r) => (
                  <span
                    key={r.slug}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
                  >
                    {r.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <InfoRow label="First name" value={me.firstName} />
            <InfoRow label="Last name" value={me.lastName} />
            <InfoRow label="Username" value={me.username ? `@${me.username}` : null} />
            <InfoRow
              label="Email"
              value={
                me.email ? `${me.email}${me.isSharedEmailAccount ? ' (shared mailbox)' : ''}` : null
              }
            />
            <InfoRow label="Mobile" value={me.phone} />
          </div>
          {!me.capabilities.canEditNames && (
            <ManagedByAdminNote>
              Your name, email, username and mobile number are managed by your administrator.
            </ManagedByAdminNote>
          )}
        </div>
      </div>
    </ProfileSectionCard>
  );
}
